import { useId, useState, type FormEvent } from "react";
import { useCreateLlmCredential } from "../../api/queries";
import type { LlmProvider } from "../../api/types";
import { Button } from "../../components/Button";
import { FormError } from "../../components/FormError";
import { Modal } from "../../components/Modal";
import { useToast } from "../../toast/ToastContext";
import "../../components/Form.css";

interface AddLlmCredentialModalProps {
  open: boolean;
  onClose: () => void;
}

export function AddLlmCredentialModal({ open, onClose }: AddLlmCredentialModalProps) {
  const providerId = useId();
  const baseUrlId = useId();
  const labelId = useId();
  const apiKeyId = useId();
  const modelsId = useId();
  const budgetId = useId();
  const { pushToast } = useToast();
  const mutation = useCreateLlmCredential();
  const [provider, setProvider] = useState<LlmProvider>("gemini");
  const [baseUrl, setBaseUrl] = useState("");
  const [label, setLabel] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [models, setModels] = useState("");
  const [budget, setBudget] = useState("");

  // Ollama is a keyless local endpoint; every other provider needs an API key.
  const keyRequired = provider !== "ollama";

  function handleClose() {
    setProvider("gemini");
    setBaseUrl("");
    setLabel("");
    setApiKey("");
    setModels("");
    setBudget("");
    mutation.reset();
    onClose();
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const submittedLabel = label.trim();
    mutation.mutate(
      {
        provider,
        baseUrl: baseUrl.trim() || undefined,
        label: submittedLabel,
        key: apiKey,
        models: models.trim() ? models.split(",").map((m) => m.trim()).filter(Boolean) : undefined,
        budgetMonthlyUsd: budget ? parseFloat(budget) : undefined,
      },
      {
        onSuccess: (created) => {
          pushToast({ title: `Credential "${created.label}" added.`, tone: "success" });
          handleClose();
        },
        onError: (err) => {
          pushToast({
            title: `Couldn't add "${submittedLabel}"`,
            description: err instanceof Error ? err.message : "Try again in a moment.",
            tone: "error",
          });
        },
      },
    );
    // Handed off to the mutation above — clear the key from state immediately (S-13).
    setApiKey("");
  }

  return (
    <Modal open={open} title="Add an LLM credential" onClose={handleClose} initialFocusId={labelId}>
      <form onSubmit={handleSubmit} noValidate>
        <p className="form-note">
          Comebye uses this key only to call the provider on the workbench's behalf — it's sealed on write and
          never shown again.
        </p>

        <label className="form-field" htmlFor={providerId}>
          <span>Provider</span>
          <select id={providerId} value={provider} onChange={(e) => setProvider(e.target.value as LlmProvider)}>
            <option value="gemini">Google Gemini (free tier)</option>
            <option value="openrouter">OpenRouter (one key, many models)</option>
            <option value="openai">OpenAI</option>
            <option value="deepseek">DeepSeek</option>
            <option value="groq">Groq</option>
            <option value="mistral">Mistral</option>
            <option value="together">Together AI</option>
            <option value="cerebras">Cerebras</option>
            <option value="ollama">Ollama (local, no key)</option>
            <option value="openai-compatible">OpenAI-compatible (self-hosted — endpoint required)</option>
          </select>
        </label>

        <label className="form-field" htmlFor={baseUrlId}>
          <span>Endpoint override (optional)</span>
          <input
            id={baseUrlId}
            type="text"
            autoComplete="off"
            spellCheck={false}
            placeholder="https://…/v1"
            value={baseUrl}
            onChange={(e) => setBaseUrl(e.target.value)}
          />
          <span className="form-field__hint">
            OpenAI-compatible base URL. Leave blank for the provider default; required for a self-hosted Ollama not on localhost.
          </span>
        </label>

        <label className="form-field" htmlFor={labelId}>
          <span>Label</span>
          <input
            id={labelId}
            type="text"
            autoComplete="off"
            spellCheck={false}
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            required
            maxLength={128}
          />
        </label>

        <label className="form-field" htmlFor={apiKeyId}>
          <span>API key</span>
          <input
            id={apiKeyId}
            type="password"
            autoComplete="off"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            required={keyRequired}
          />
          <span className="form-field__hint">
            {keyRequired
              ? "Sealed on write. Never shown again — add a new credential later if you need to replace it."
              : "Ollama is a local, keyless endpoint — leave this blank."}
          </span>
        </label>

        <label className="form-field" htmlFor={modelsId}>
          <span>Model allowlist</span>
          <input
            id={modelsId}
            type="text"
            autoComplete="off"
            spellCheck={false}
            value={models}
            onChange={(e) => setModels(e.target.value)}
          />
          <span className="form-field__hint">
            Comma-separated model IDs to restrict usage, or leave blank for all.
          </span>
        </label>

        <label className="form-field" htmlFor={budgetId}>
          <span>Monthly budget (USD)</span>
          <input
            id={budgetId}
            type="number"
            min={0}
            step={0.01}
            value={budget}
            onChange={(e) => setBudget(e.target.value)}
          />
          <span className="form-field__hint">Monthly spend cap in USD, or leave blank for unlimited.</span>
        </label>

        {/*
          The server validates the credential against the provider before storing it,
          so a failure here is usually actionable and specific — "this key was
          rejected", or "your account may not be entitled to <model>; choose another".
          Render it verbatim and PERSISTENTLY (Q-7): a toast that fades takes the
          instruction with it, and this is the message the user has to act on.
        */}
        {mutation.isError && (
          <FormError>
            {mutation.error instanceof Error ? mutation.error.message : "Couldn't add the credential. Try again."}
          </FormError>
        )}

        <div className="form-actions">
          <Button type="button" variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={mutation.isPending || !label.trim() || (keyRequired && !apiKey)}>
            {mutation.isPending ? "Adding…" : "Add credential"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
